import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoadingService } from './loading.service';

export interface RequestOTPRequest {
  email: string;
}

export interface RequestOTPResponse {
  message: string;
  email: string;
  expires_in_seconds: number;
}

export interface VerifyOTPRequest {
  email: string;
  otp_code: string;
}

export interface VerifyOTPResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: SuperAdminUser;
}

export interface SuperAdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_super_admin: boolean;
}

export interface ResendOTPRequest {
  email: string;
}

export interface ResendOTPResponse {
  message: string;
  can_resend_at: string;
}

interface StoredSuperAdminSession {
  accessToken: string;
  tokenExpiryMs: number;
  user: SuperAdminUser;
}

@Injectable({
  providedIn: 'root'
})
export class SuperAdminAuthService {
  private readonly sessionStorageKey = 'parseeasy_superadmin_session';

  private currentUserSubject = new BehaviorSubject<SuperAdminUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  /** Access token kept in memory; mirrored to sessionStorage for tab refresh only */
  private accessToken: string | null = null;
  private tokenExpiryMs: number | null = null;

  constructor(
    private http: HttpClient,
    private router: Router,
    private loadingService: LoadingService
  ) {
    this.restoreSession();
  }

  requestOTP(email: string): Observable<RequestOTPResponse> {
    const request: RequestOTPRequest = { email };
    return this.http.post<RequestOTPResponse>(
      `${environment.apiUrl}/superadmin/auth/request-otp`,
      request
    );
  }

  verifyOTP(email: string, otpCode: string): Observable<VerifyOTPResponse> {
    const request: VerifyOTPRequest = { email, otp_code: otpCode };
    return this.http.post<VerifyOTPResponse>(
      `${environment.apiUrl}/superadmin/auth/verify-otp`,
      request
    ).pipe(
      tap(response => this.handleAuthResponse(response))
    );
  }

  resendOTP(email: string): Observable<ResendOTPResponse> {
    const request: ResendOTPRequest = { email };
    return this.http.post<ResendOTPResponse>(
      `${environment.apiUrl}/superadmin/auth/resend-otp`,
      request
    );
  }

  logout(): void {
    this.clearAuthData();
    this.router.navigate(['/super-admin/login']);
  }

  clearAuthData(): void {
    sessionStorage.removeItem(this.sessionStorageKey);
    this.accessToken = null;
    this.tokenExpiryMs = null;
    this.currentUserSubject.next(null);
    this.loadingService.reset();
  }

  getAccessToken(): string | null {
    if (!this.accessToken) {
      return null;
    }

    if (this.isTokenExpired()) {
      this.clearAuthData();
      return null;
    }

    return this.accessToken;
  }

  hasSuperAdminRole(user: SuperAdminUser | null = this.getCurrentUser()): boolean {
    if (!user) {
      return false;
    }
    if (user.is_super_admin) {
      return true;
    }
    return String(user.role || '').toLowerCase() === 'super_admin';
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  isTokenExpired(): boolean {
    if (this.tokenExpiryMs) {
      return Date.now() > this.tokenExpiryMs;
    }

    if (this.accessToken) {
      return this.isJwtExpired(this.accessToken);
    }

    return true;
  }

  private isJwtExpired(token: string): boolean {
    try {
      const segment = token.split('.')[1];
      if (!segment) {
        return true;
      }
      const padded = segment.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(segment.length / 4) * 4, '=');
      const payload = JSON.parse(atob(padded));
      if (payload.exp) {
        return Date.now() > payload.exp * 1000;
      }
      return false;
    } catch {
      return true;
    }
  }

  getCurrentUser(): SuperAdminUser | null {
    return this.currentUserSubject.value;
  }

  private handleAuthResponse(response: VerifyOTPResponse): void {
    if (!response?.access_token) {
      throw new Error('OTP verification did not return an access token');
    }

    const rawUser = response.user || ({} as SuperAdminUser);
    const role = String(rawUser.role || '').toLowerCase();
    const user: SuperAdminUser = {
      ...rawUser,
      is_super_admin: rawUser.is_super_admin === true || role === 'super_admin'
    };

    const expiresIn = Number(response.expires_in);
    this.accessToken = response.access_token;
    this.tokenExpiryMs = Date.now() + (Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : 3600) * 1000;
    this.currentUserSubject.next(user);
    this.persistSession();
  }

  private restoreSession(): void {
    const raw = sessionStorage.getItem(this.sessionStorageKey);
    if (!raw) {
      return;
    }

    try {
      const session = JSON.parse(raw) as StoredSuperAdminSession;
      if (!session.accessToken || !session.user) {
        sessionStorage.removeItem(this.sessionStorageKey);
        return;
      }

      this.accessToken = session.accessToken;
      this.tokenExpiryMs = session.tokenExpiryMs;
      this.currentUserSubject.next(session.user);

      if (this.isTokenExpired()) {
        this.clearAuthData();
      }
    } catch {
      sessionStorage.removeItem(this.sessionStorageKey);
    }
  }

  private persistSession(): void {
    if (!this.accessToken || !this.currentUserSubject.value) {
      return;
    }

    const session: StoredSuperAdminSession = {
      accessToken: this.accessToken,
      tokenExpiryMs: this.tokenExpiryMs ?? Date.now(),
      user: this.currentUserSubject.value
    };

    sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(session));
  }
}
